/**
 * Custom slider control matching the app's dark theme.
 *
 * Built from scratch (no native <input type="range">) for consistent
 * cross-browser appearance and touch-friendly interaction:
 *  - 44px minimum touch target height
 *  - Pointer capture for smooth dragging
 *  - Visual track/fill/thumb styled to the glassmorphic dark UI
 *
 * Props mirror a simplified range input API so it's a drop-in replacement.
 */

import { useCallback, useEffect, useRef } from "react";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_BORDER_ACCENT,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_SUBTLE,
} from "./tokens.ts";
import { useUiInteractionStore } from "./uiInteractionStore.ts";

/** Desaturated grey at a similar perceived lightness to COLOR_ACCENT. */
const COLOR_ZERO_HANDLE = COLOR_TEXT_SUBTLE;

export interface SliderProps {
  /** Current value */
  value: number;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step granularity (default: 1). Set to 0 for continuous (no snapping). */
  step?: number;
  /**
   * Discrete stop values. When provided, the slider snaps to the nearest
   * stop rather than interpolating linearly between min and max.
   * The stops array must be sorted ascending.
   */
  stops?: number[];
  /** Called on every drag frame */
  onChange: (value: number) => void;
  /** Called once when the drag begins */
  onStart?: () => void;
  /** Called once when the interaction ends */
  onEnd?: () => void;
  /** Whether the slider is disabled */
  disabled?: boolean;
  /** Accent color for the fill and thumb (default: COLOR_ACCENT) */
  accent?: string;
  /** Default value — double-click / double-tap the thumb to reset. */
  defaultValue?: number;
  /**
   * When set, this value maps to the visual centre (50%) of the track.
   * [min → midpoint] occupies the left half, [midpoint → max] the right.
   * Gives more slider real-estate to the denser end of the range.
   */
  midpoint?: number;
  /**
   * When `true`, dragging this slider dims all overlay chrome on mobile so
   * the user can see the viewport change unobscured.  Opt in **only** for
   * sliders whose changes are reflected live in the 3D viewport.
   * Configuration-only sliders (whose changes aren't visible until applied)
   * should leave this off so the UI stays fully legible during adjustment.
   */
  dimChromeOnDrag?: boolean;
}

// ---------------------------------------------------------------------------
// Piecewise-linear midpoint helpers — shared with PaletteSlider
// ---------------------------------------------------------------------------

/** Convert a 0-1 track ratio to a domain value, respecting an optional midpoint. */
export function ratioToValue(
  ratio: number,
  min: number,
  max: number,
  midpoint?: number,
): number {
  if (midpoint === undefined) return min + ratio * (max - min);
  if (ratio <= 0.5) return min + (ratio / 0.5) * (midpoint - min);
  return midpoint + ((ratio - 0.5) / 0.5) * (max - midpoint);
}

/** Convert a domain value to a 0-1 track ratio, respecting an optional midpoint. */
export function valueToRatio(
  value: number,
  min: number,
  max: number,
  midpoint?: number,
): number {
  if (midpoint === undefined) {
    const range = max - min;
    return range > 0 ? (value - min) / range : 0;
  }
  if (value <= midpoint) {
    const range = midpoint - min;
    return range > 0 ? ((value - min) / range) * 0.5 : 0;
  }
  const range = max - midpoint;
  return range > 0 ? 0.5 + ((value - midpoint) / range) * 0.5 : 1;
}

export function Slider({
  value,
  min,
  max,
  step = 1,
  stops,
  onChange,
  onStart,
  onEnd,
  disabled,
  accent = COLOR_ACCENT,
  defaultValue,
  midpoint,
  dimChromeOnDrag = false,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const lastTapRef = useRef(0);
  const draggingRef = useRef(false);
  const onEndRef = useRef(onEnd);

  useEffect(() => {
    onEndRef.current = onEnd;
  }, [onEnd]);

  // Safety net: if this slider unmounts mid-drag (rare, e.g. a panel closes
  // while the pointer is still captured), release the global drag count so
  // chrome doesn't stay dim forever.
  useEffect(() => {
    return () => {
      const hadActiveInteraction = startedRef.current || draggingRef.current;
      if (draggingRef.current) {
        useUiInteractionStore.getState().endSliderDrag();
        draggingRef.current = false;
      }
      startedRef.current = false;
      if (hadActiveInteraction) onEndRef.current?.();
    };
  }, []);

  const clamp = useCallback(
    (v: number) => {
      if (stops && stops.length > 0) {
        // Snap to nearest stop
        let best = stops[0];
        let bestDist = Math.abs(v - best);
        for (let i = 1; i < stops.length; i++) {
          const dist = Math.abs(v - stops[i]);
          if (dist < bestDist) {
            best = stops[i];
            bestDist = dist;
          }
        }
        return best;
      }
      if (step > 0) {
        const snapped = Math.round(v / step) * step;
        return Math.min(max, Math.max(min, snapped));
      }
      return Math.min(max, Math.max(min, v));
    },
    [min, max, step, stops],
  );

  const valueFromPointer = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return value;
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      if (stops && stops.length > 0) {
        // Map ratio to stop index space for even spacing
        const idx = ratio * (stops.length - 1);
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, stops.length - 1);
        // Snap to nearest
        return idx - lo < 0.5 ? stops[lo] : stops[hi];
      }
      return clamp(ratioToValue(ratio, min, max, midpoint));
    },
    [min, max, value, clamp, stops, midpoint],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startedRef.current = false;
      const v = valueFromPointer(e.clientX);
      if (!startedRef.current) {
        onStart?.();
        startedRef.current = true;
        if (dimChromeOnDrag) {
          draggingRef.current = true;
          useUiInteractionStore.getState().beginSliderDrag();
        }
      }
      onChange(v);
    },
    [disabled, valueFromPointer, onChange, onStart, dimChromeOnDrag],
  );

  /** Double-click / double-tap the thumb to reset to defaultValue. */
  const handleThumbPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || defaultValue === undefined) return;
      const now = Date.now();
      if (now - lastTapRef.current < 350) {
        // Double-tap detected — reset and swallow the event.
        e.preventDefault();
        e.stopPropagation();
        onStart?.();
        onChange(defaultValue);
        onEnd?.();
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    },
    [disabled, defaultValue, onChange, onStart, onEnd],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || !startedRef.current) return;
      onChange(valueFromPointer(e.clientX));
    },
    [disabled, valueFromPointer, onChange],
  );

  const handlePointerUp = useCallback(() => {
    const wasStarted = startedRef.current;
    startedRef.current = false;
    if (draggingRef.current) {
      draggingRef.current = false;
      useUiInteractionStore.getState().endSliderDrag();
    }
    if (wasStarted) onEnd?.();
  }, [onEnd]);

  // Compute fill percentage — evenly spaced for stops mode
  let pct: number;
  if (stops && stops.length > 1) {
    const idx = stops.indexOf(value);
    pct =
      idx >= 0
        ? (idx / (stops.length - 1)) * 100
        : ((value - stops[0]) / (stops[stops.length - 1] - stops[0])) * 100;
  } else {
    pct = valueToRatio(value, min, max, midpoint) * 100;
  }

  const isAtDefault = value === (defaultValue ?? 0);

  return (
    <div
      ref={trackRef}
      // `data-no-drag` opts this control out of FloatingCommandMenu's
      // background drag handler so slider scrubs aren't mistaken for
      // panel drags when the slider sits inside a floating menu.
      data-no-drag
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: "relative",
        height: 28,
        display: "flex",
        alignItems: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.35 : 1,
        touchAction: "none",
        flex: 1,
      }}
    >
      {/* Track background */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          height: 4,
          borderRadius: 2,
          background: COLOR_BORDER,
        }}
      />
      {/* Stop ticks */}
      {stops &&
        stops.length > 1 &&
        stops.map((_, i) => {
          const tickPct = (i / (stops.length - 1)) * 100;
          return (
            <div
              key={tickPct}
              style={{
                position: "absolute",
                left: `${tickPct}%`,
                width: 1,
                height: 10,
                background: COLOR_BORDER_ACCENT,
                transform: "translateX(-0.5px)",
                pointerEvents: "none",
              }}
            />
          );
        })}
      {/* Fill */}
      <div
        style={{
          position: "absolute",
          left: 0,
          width: `${pct}%`,
          height: 4,
          borderRadius: 2,
          background: disabled
            ? COLOR_TEXT_DISABLED
            : isAtDefault
              ? COLOR_ZERO_HANDLE
              : accent,
          opacity: 0.8,
        }}
      />
      {/* Thumb */}
      <div
        onPointerDown={handleThumbPointerDown}
        style={{
          position: "absolute",
          left: `${pct}%`,
          width: 14,
          height: 14,
          borderRadius: 7,
          background: disabled
            ? COLOR_TEXT_DISABLED
            : isAtDefault
              ? COLOR_ZERO_HANDLE
              : accent,
          transform: "translateX(-50%)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
        }}
      />
    </div>
  );
}
